import { UnleashClient } from 'unleash-proxy-client'
import Config from '@/config'
import sentry from './sentry'
import { flags } from '@/utils/abtest.utils'
import { saveCachedUnleashContext, getCachedUnleashContext } from '@/vuex/modules/loading-page/loading-page.utils'

class UnleashService {
  constructor(additionalParams = {}) {
    try {
      this.unleashClient = new UnleashClient({
        url: Config.unleashEndpoint,
        clientKey: Config.unleashClientKey,
        appName: 'presenter',
        createAbortController: () => null,
        // This is the on-update function for real-time updates from Unleash to continuously refresh feature flags from the Unleash server.
        // Developers should carefully consider which features to use with this function, as it needs to handle cases where a feature might be toggled on or off while users are actively using it.
        // Additionally, there may be unforeseen cases to account for.
        refreshInterval: 0,
        ...additionalParams,
      })
      this.ready = new Promise((resolve) => this.unleashClient.on('ready', resolve))
    } catch (error) {
      sentry.captureException(error)
      this.unleashClient = null
      // in case of error, don’t leave `ready` hanging
      this.ready = Promise.resolve()
    }
  }

  // Ideally, Only 2 requests to Unleash proxy should be made
  // 1 for the anonymous (with sessionId is the stickiness)
  // 2 for the user context (with userId is the stickiness)
  start() {
    if (flags.canUseNewUnleashMechanism) {
      this.startV2()
    } else {
      this.startV1()
    }
  }

  startV1() {
    this.unleashClient.start()
  }

  startV2() {
    // only start once!
    // can create a v2 function to easier to flag
    if (this.unleashClient && !this.isStarted) {
      this.isStarted = true
      if (flags.canUseCacheProfileOnLoad) {
        this.unleashClient.on('update', () => {
          // whenever there is update, capture the current context
          // and store it to bootstrap
          const context = this.unleashClient.getContext()
          saveCachedUnleashContext(context)
        })
      }
      this.unleashClient.start()
    }
  }

  isFeatureEnabled(featureName) {
    if (!this.unleashClient) {
      return false
    }

    try {
      return this.unleashClient.isEnabled(featureName)
    } catch (error) {
      return false
    }
  }

  getVariant(featureName) {
    if (!this.unleashClient) {
      return { name: 'disabled', enabled: false }
    }

    try {
      return this.unleashClient.getVariant(featureName)
    } catch (error) {
      return { name: 'disabled', enabled: false }
    }
  }
}
let params = {}
if (flags.canUseCacheProfileOnLoad) {
  const cachedUnleashContext = getCachedUnleashContext()
  params = cachedUnleashContext ? {
    experimental: {
      // make it 1 week
      // this only affects the initialFetch
      // we always trigger fetch again when updateContext
      // https://github.com/Unleash/unleash-js-sdk/blob/85e83a7f060d601bca1f6825e0f9501394159000/src/index.ts#L544
      togglesStorageTTL: 60 * 60 * 24 * 7 * 1000,
    },
    context: cachedUnleashContext,
  } : {}
}
export default new UnleashService(params)
